import { api } from './client';
import type {
  CreateEntryRequest,
  CreateEntryResponse,
  EntryBootstrapResponse,
} from '../types/entry';

export type BulkPreviewEntry = { number: string; first: string; second: string };
export type ClipboardEntry = { number: string | number; first?: string | number | null; second?: string | number | null };

export const formatEntriesForClipboard = (rows: ClipboardEntry[]) => rows
  .map((row) => `${row.number}.${row.first ?? 0}+${row.second ?? 0}`)
  .join('\n');

export function getEntryBootstrap(signal?: AbortSignal): Promise<EntryBootstrapResponse> {
  return api<EntryBootstrapResponse>(
    '/api/user-entry/bootstrap',
    signal ? { signal } : undefined,
  );
}

export function createEntry(body: CreateEntryRequest, signal?: AbortSignal): Promise<CreateEntryResponse> {
  return api<CreateEntryResponse>('/api/entries', {
    method: 'POST',
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
}

export function deleteEntries(ids: string[], batchId?: string) {
  return api<{ ok: true; deletedCount: number; walletRefund: string }>('/api/entries/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids, ...(batchId ? { batchId } : {}) }),
  });
}

export function getSmsFixErrorsState() {
  return api<{ ok: true; enabled: boolean }>('/api/entries/sms/fix-errors');
}

export function setSmsFixErrorsState(enabled: boolean) {
  return api<{ ok: true; enabled: boolean }>('/api/entries/sms/fix-errors', {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

export function previewSms(drawId: string, text: string) {
  return api<{ ok: true; created: number; entries: BulkPreviewEntry[] }>(
    '/api/entries/sms/preview',
    { method: 'POST', body: JSON.stringify({ drawId, text }) },
  );
}

export function commitSms(drawId: string, text: string, idempotencyKey: string) {
  return api<{ ok: true; created: number; availableBalance?: string }>('/api/entries/sms', {
    method: 'POST',
    body: JSON.stringify({ drawId, text, idempotencyKey }),
  });
}

export function previewPalti(drawId: string, mode: 'akra' | 'tndola' | 'pangora', text: string) {
  return api<{ ok: true; created: number; totalAmount: string; entries: BulkPreviewEntry[] }>(
    '/api/entries/palti/preview',
    { method: 'POST', body: JSON.stringify({ drawId, mode, text }) },
  );
}

export function commitPalti(drawId: string, mode: 'akra' | 'tndola' | 'pangora', text: string, idempotencyKey: string) {
  return api<{ ok: true; created: number; availableBalance?: string }>('/api/entries/palti', {
    method: 'POST',
    body: JSON.stringify({ drawId, mode, text, idempotencyKey }),
  });
}

export function getLastBulk(drawId: string) {
  const qs = new URLSearchParams({ drawId });
  return api<{ ok: true; found: boolean; batchId?: string; entryIds?: string[]; count?: number; operation?: string }>(`/api/entries/last-bulk?${qs.toString()}`);
}

export function getSavedSheets() {
  return api<{ ok: true; sheets: Array<{ id: string; sheetName: string; drawTitle: string; drawDate: string; totalEntries: number; firstTotal: string; secondTotal: string; grandTotal: string; createdAt: string }> }>('/api/sheets');
}

export function deleteSavedSheet(id: string) {
  return api<{ ok: true; deleted: boolean; deletedCount: number; walletRefund: string; sheetDeleted: boolean }>(`/api/sheets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export interface SavedSheetCreateResult {
  id: string;
  sheetName: string;
  totalEntries: number;
  firstTotal: string;
  secondTotal: string;
  grandTotal: string;
  createdAt: string;
}

export function saveCurrentSheet(drawId: string, sheetName: string) {
  return api<{ ok: true; sheet: SavedSheetCreateResult }>('/api/sheets', {
    method: 'POST',
    body: JSON.stringify({ drawId, sheetName }),
  });
}

export function getBookDetails(drawId: string) {
  const qs = new URLSearchParams({ drawId, page: '1', perPage: '50', search: '' });
  return api<Record<string, unknown>>(`/api/book-details?${qs.toString()}`);
}

export function getPaymentHistory() {
  return api<{ ok: true; transactions: unknown[] }>('/api/my/payment-history');
}
